function normalizeLabel(s) {
  return String(s || '').trim().toLowerCase();
}

function labelsRoughlyMatch(apiLabel, gameLabel) {
  const a = normalizeLabel(apiLabel);
  const b = normalizeLabel(gameLabel);
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.includes(b) || b.includes(a)) return true;
  return false;
}

function rowTeamFields(row) {
  if (row.team) {
    return {
      name: row.team.name,
      shortName: row.team.shortName,
      tla: row.team.tla
    };
  }
  return {
    name: row.name,
    shortName: row.shortName,
    tla: row.tla
  };
}

function rowMatchesTeam(row, teamLabel) {
  const t = rowTeamFields(row);
  // TLAs are short codes (e.g. STA) — exact match only. Substring matching
  // falsely highlights Start for Fredrikstad (…sta…) and Tromsø for Lillestrøm (…tro…).
  if (t.tla && normalizeLabel(t.tla) === normalizeLabel(teamLabel)) return true;
  const names = [t.name, t.shortName].filter(Boolean);
  return names.some((n) => labelsRoughlyMatch(n, teamLabel));
}

export function rowHighlightsMatch(row, homeTeam, awayTeam) {
  return rowMatchesTeam(row, homeTeam) || rowMatchesTeam(row, awayTeam);
}

function tableContainsBothTeams(table, homeTeam, awayTeam) {
  if (!Array.isArray(table)) return false;
  let hasHome = false;
  let hasAway = false;
  for (const row of table) {
    if (rowMatchesTeam(row, homeTeam)) hasHome = true;
    if (rowMatchesTeam(row, awayTeam)) hasAway = true;
    if (hasHome && hasAway) return true;
  }
  return false;
}

/**
 * Normalize canonical or legacy football-data standings payload to table blocks.
 * @param {object|Array} standings
 * @returns {Array<{ type?: string, group?: string, table: Array }>}
 */
function normalizeStandingsBlocks(standings) {
  if (!standings) return [];

  if (Array.isArray(standings.tables)) {
    return standings.tables.map((t) => ({
      type: t.type,
      group: t.group ?? null,
      table: t.rows || []
    }));
  }

  if (Array.isArray(standings)) {
    return standings.map((s) => ({
      type: s.type,
      group: s.group ?? null,
      table: s.table || s.rows || []
    }));
  }

  if (Array.isArray(standings.standings)) {
    return normalizeStandingsBlocks(standings.standings);
  }

  return [];
}

/**
 * Pick the standings table for a match. World Cup group games use the group table
 * that contains both teams; domestic leagues use the TOTAL table.
 */
export function pickStandingsTableForMatch(standings, homeTeam, awayTeam) {
  const blocks = normalizeStandingsBlocks(standings);
  if (blocks.length === 0) {
    return { rows: [], group: null };
  }

  const grouped = blocks.filter((s) => s.group != null && Array.isArray(s.table));
  if (grouped.length > 0) {
    const match = grouped.find((s) => tableContainsBothTeams(s.table, homeTeam, awayTeam));
    if (match) {
      return { rows: match.table, group: match.group };
    }
    return { rows: [], group: null };
  }

  const total = blocks.find((s) => s.type === 'TOTAL');
  const table = (total || blocks[0])?.table;
  return {
    rows: Array.isArray(table) ? table : [],
    group: null
  };
}

export function formatStandingsGroupLabel(group) {
  if (!group) return null;
  const match = String(group).match(/^GROUP_([A-L])$/i);
  if (match) return `Group ${match[1]}`;
  return String(group).replace(/_/g, ' ');
}
